import {html} from 'lit';
import {
	selectSpcIsSatisfied
} from './selectors';

import {solarSectionConfig} from './solar/solarSectionConfig';
import {olivioSectionConfig} from './olivio/olivioSectionConfig';
import {lineColumnBollard, lineColumnIntern, lineBollardIntern, lineSingular, lineSingularIntern} from './line/lineSectionConfig';
import {lightProfile} from './lightProfile/lightProfileSectionConfig';

// section configuration
// used in statusSlice

export const generalAttributeOptions = {
	'root::CX_Region': {
		hidden: true
	},
	'root::HS_HousingSize': {
		attributePriority: 'basic'
	},
	'root::HS_Base': {
		attributePriority: 'basic',
		doNotShowWhenNoneIsOnlyOption: true
	},

	'root::HS_LightEmission': {
		hidden: false,
		attributePriority: 'basic',
	},
	'root::HS_LightEmissionCA': {
		attributePriority: 'basic',
		hidden: true
	},
	'root::HS_LightEmissionCAFilter': {
		attributePriority: 'basic',
		hidden: false
	},
	'root::HS_UpLight': {
		attributePriority: 'basic',
		hidden: true
	},

	'root::LE_LightEngineModuleSize_1': {
		attributePriority: 'basic'
	},
	'root::LE_LightEngineModuleSize_2': {
		attributePriority: 'basic'
	},
	'root::LE_ColourTemperature_1': {
		attributePriority: 'basic'
	},
	'root::LE_OpticalVersion_1': {
		attributePriority: 'basic'
	},
	'root::LE_ReflectorColour_1': {
		attributePriority: 'basic'
	},
	'root::HS_CircularBaseProfile': {
		attributePriority: 'basic',
		displayStyle: 'listSelect',
		addon: html`
			<light-profile-spc-widget-addon></light-profile-spc-widget-addon>`
	},
	'root::LU_LegacyArticleNumberGerman': {
		displayStyle: 'comboSelect',
		hideDisabledValues: true
	},
	'root::LU_LegacyArticleNumberFrench': {
		displayStyle: 'comboSelect',
		hideDisabledValues: true
	},
	'root::LU_LegacyArticleNumberGerman_1': {
		displayStyle: 'comboSelect',
		hideDisabledValues: true
	},
	'root::LU_LegacyArticleNumberGerman_2': {
		displayStyle: 'comboSelect',
		hideDisabledValues: true
	},
	'root::LU_Housing_LegacyArticleNumberGerman': {
		displayStyle: 'comboSelect',
		hideDisabledValues: true
	},
	'root::LU_Battery_LegacyArticleNumberGerman': {
		displayStyle: 'comboSelect',
		hideDisabledValues: true
	},
	'root::LU_SolarUnit_LegacyArticleNumberGerman': {
		displayStyle: 'comboSelect',
		hideDisabledValues: true
	},
	'root::LE_DimmingLevel_1': {
		attributePriority: 'basic',
		hideDisabledValues: true,
		specSheetValuesDisplay: 'max',
		hidden: true,
		unit: 'lm'
	},
	'root::LE_LightDistribution_1': {
		attributePriority: 'basic',
		hideDisabledValues: false
	},
	'root::LE_LightDistribution_2': {
		attributePriority: 'basic',
		hideDisabledValues: false
	},
	'root::HS_HousingZhaga': {
		doNotShowWhenNoneIsOnlyOption: true
	},
	'root::EB_Zhaga_1': {
		doNotShowWhenNoneIsOnlyOption: true
	}
};


export const sectionConfigs = {
	General: [
		{
			id: 'default',
			label: null,
			visual: null,
			features: [],
			useAttributesView: true,
			attributeOptions: generalAttributeOptions,
			widgets: [
				html`<model-select></model-select>`,
			],
			widgetsBasic: [
				html`<vertical-slider
					.title=${{de: 'Dimmstufe', en: 'Dimm Level', fr: 'Niveau de gradation'}}
					.isSatisfied= ${selectSpcIsSatisfied}
					action='decisions/setDimmLevel'
				></vertical-slider>`,
			],
			widgetsEnd: []
		}
	],
	ColumnBollard: lineColumnBollard,
	ColumnIntern: lineColumnIntern,
	BollardIntern: lineBollardIntern,
	Singular: lineSingular,
	SingularIntern: lineSingularIntern,
	LightProfile: lightProfile,
	Aeto: solarSectionConfig,
	Olivio: olivioSectionConfig,
	Start: [
		{
			id: 'family',
			label: {
				de: 'Familie wählen',
				en: 'Select Family',
				fr: 'Select Family',
			},
			visual: null,
			features: [],
			widgets: [
				html`<family-select></family-select>`
			]
		}
	],
	Default: [
		{
			id: 'default',
			label: {
				de: 'Default',
				en: 'Default',
				fr: 'Default',
			},
			visual: null,
			features: [
			]
		}
	]
};

// pick sectionConfig depending on featureModelId
export const sections = {
	TheLine: {
		default: sectionConfigs.ColumnBollard,
		ColumnExtern: sectionConfigs.ColumnBollard,
		BollardExtern: sectionConfigs.ColumnBollard,

		Twist06Extern: sectionConfigs.Singular,
		Twist12Extern: sectionConfigs.Singular,

		PoleLateralExtern: sectionConfigs.Singular,
		PoleTopExtern: sectionConfigs.Singular,
		WallSurfaceExtern: sectionConfigs.Singular,
		WallRecessedExtern: sectionConfigs.Singular,

		ColumnIntern: sectionConfigs.ColumnIntern,
		BollardIntern: sectionConfigs.BollardIntern,

		Twist06Intern: sectionConfigs.SingularIntern,
		Twist12Intern: sectionConfigs.SingularIntern,

		PoleLateralIntern: sectionConfigs.SingularIntern,
		PoleTopIntern: sectionConfigs.SingularIntern,
		WallSurfaceIntern: sectionConfigs.SingularIntern,
		WallRecessedIntern: sectionConfigs.SingularIntern,
	},
	Avanza: {
		default: sectionConfigs.General,
		AvanzaExtern: sectionConfigs.General,
		AvanzaIntern: sectionConfigs.General,
	},
	Tal: {
		default: sectionConfigs.General
	},
	Saturn: {
		default: sectionConfigs.General
	},
	Lanova: {
		default: sectionConfigs.General
	},
	Aya: {
		default: sectionConfigs.General
	},
	Yloo: {
		default: sectionConfigs.General
	},
	LightProfile: {
		default: sectionConfigs.LightProfile
	},
	Aeto: {
		default: sectionConfigs.Aeto
	},
	Aeto2: {
		default: sectionConfigs.General
	},
	Start: {
		default: sectionConfigs.Start
	},
	Olivio: {
		default: sectionConfigs.Olivio
	},
	Olivio2: {
		default: sectionConfigs.General
	},
	default: sectionConfigs.General
};

export function getSectionConfig(family, modelId) {
	// console.log('getSectionConfig', family, modelId);
	if (sections[family]) {
		return sections[family][modelId] || sections[family].default;
	} else {
		return sections.default;
	}

}

